import ErrorOutline from "@mui/icons-material/ErrorOutline";
import ExpandMore from "@mui/icons-material/ExpandMore";
import { Box, Collapse, Stack, Typography } from "@mui/material";
import { useState } from "react";

import { ScoutToolPart } from "./message/scoutChatMessage";
import { renderToolTitle } from "./toolRenderers";

const SECONDARY_TEXT = "rgba(0, 0, 0, 0.6)";

function isToolRunning(tool: ScoutToolPart): boolean {
  return tool.toolState === "input_streaming" || tool.toolState === "input_available";
}

export function ToolCallGroup({ toolParts }: { toolParts: ScoutToolPart[] }) {
  const [expanded, setExpanded] = useState(false);

  if (toolParts.length === 0) return null;

  const anyRunning = toolParts.some(isToolRunning);
  const headerContent =
    toolParts.length > 1
      ? anyRunning
        ? "I'm checking on several resources"
        : "Checked on several resources"
      : renderToolTitle({ tool: toolParts[0], isRunning: isToolRunning(toolParts[0]) });

  return (
    <Box sx={{ my: 0.5 }}>
      <Stack
        direction="row"
        alignItems="center"
        spacing={1}
        onClick={() => setExpanded((v) => !v)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setExpanded((v) => !v);
          }
        }}
        sx={{
          cursor: "pointer",
          userSelect: "none",
          color: SECONDARY_TEXT,
          "&:hover": { color: "text.primary" },
        }}
      >
        <Typography variant="body1" component="div" sx={{ fontWeight: 400, color: SECONDARY_TEXT }}>
          {headerContent}
        </Typography>
        <ExpandMore
          fontSize="small"
          sx={{
            transition: "transform 150ms ease",
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
          }}
        />
      </Stack>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <Stack spacing={1} sx={{ mt: 0.75 }}>
          {toolParts.map((tool) => (
            <ToolCallRow key={tool.id} tool={tool} />
          ))}
        </Stack>
      </Collapse>
    </Box>
  );
}

function ToolCallRow({ tool }: { tool: ScoutToolPart }) {
  const isError = tool.toolState === "output_error";

  return (
    <Stack
      direction="row"
      alignItems="center"
      spacing={1}
      sx={{ color: isError ? "error.main" : SECONDARY_TEXT }}
    >
      {isError && <ErrorOutline fontSize="inherit" color="error" />}
      <Typography variant="body2" component="div">
        {renderToolTitle({ tool, isRunning: isToolRunning(tool) })}
      </Typography>
    </Stack>
  );
}
